<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useEquipmentsStore } from '@/store/equipments.js'
import { resolveImageUrl } from '@/composables/useImageUrl'

const store = useEquipmentsStore()
const router = useRouter()
const loading = ref(true)

onMounted(async () => {
  try {
    await store.fetchCategories()
  } finally {
    loading.value = false
  }
})

const parentCategories = computed(() =>
  store.categories.filter(c => !c.parent)
)

// Category search — matches parent name, description, or any subcategory name
const categorySearch = ref('')
const filteredCategories = computed(() => {
  const q = categorySearch.value.trim().toLowerCase()
  if (!q) return parentCategories.value
  return parentCategories.value.filter(c =>
    c.name.toLowerCase().includes(q) ||
    (c.description || '').toLowerCase().includes(q) ||
    (c.subcategories || []).some(s => s.name.toLowerCase().includes(q))
  )
})

const getAdCount = (category) => Number(category?.ad_count) || 0

const goToCategory = async (slug) => {
  await router.push({ name: 'category-details', query: { cat: slug } })
  window.location.reload()
}

const getImageUrl = (url) => resolveImageUrl(url)

const imgErrors = ref(new Set())
const handleImageError = (event, category) => {
  imgErrors.value.add(category.id)
}
</script>

<template>
  <div class="min-h-screen bg-theme-bg">
    <!-- Header -->
    <div class="bg-theme-bg2 border-b border-theme-border">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h1 class="text-3xl sm:text-4xl font-bold text-theme-text">Categories</h1>
            <p class="mt-2 text-theme-muted text-sm sm:text-base">
              Browse equipment by category to find exactly what you need
            </p>
          </div>
          <RouterLink
            :to="{ name: 'browse' }"
            class="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 font-semibold rounded-lg shadow-md transition-all duration-200 hover:shadow-lg text-sm whitespace-nowrap"
          >
            <i class="pi pi-th-large text-sm"></i>
            All Items
          </RouterLink>
        </div>

        <!-- Search bar (always visible) + mobile All Items button -->
        <div class="mt-4 flex items-center gap-3">
          <RouterLink
            :to="{ name: 'browse' }"
            class="sm:hidden flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition text-sm"
          >
            <i class="pi pi-th-large text-xs"></i>
            All Items
          </RouterLink>
          <div class="relative flex-1">
            <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted text-xs"></i>
            <input
              v-model="categorySearch"
              type="text"
              placeholder="Search categories…"
              class="w-full pl-8 pr-3 py-2 rounded-lg border border-theme-input-border bg-theme-input text-theme-input-text text-sm placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Categories Grid -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading -->
      <div v-if="loading" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        <div v-for="i in 12" :key="i" class="animate-pulse">
          <div class="bg-theme-bg2 rounded-xl overflow-hidden">
            <div class="h-40 bg-neutral-300 dark:bg-neutral-700"></div>
            <div class="p-4">
              <div class="h-4 bg-neutral-300 dark:bg-neutral-700 rounded w-3/4"></div>
              <div class="h-3 bg-neutral-300 dark:bg-neutral-700 rounded w-1/2 mt-2"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="parentCategories.length === 0" class="py-16 text-center">
        <i class="pi pi-inbox text-5xl text-theme-muted mb-4"></i>
        <p class="text-theme-muted text-lg">No categories available yet</p>
      </div>

      <!-- Categories -->
      <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        <button
          v-for="category in filteredCategories"
          :key="category.id"
          @click="goToCategory(category.slug)"
          class="group text-left bg-theme-card rounded-xl overflow-hidden border border-theme-border
                 shadow-theme-card hover:shadow-theme-modal transition-all duration-300
                 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <!-- Image -->
          <div class="relative h-36 sm:h-44 bg-theme-bg2 overflow-hidden">
            <img
              v-if="category.image && !imgErrors.has(category.id)"
              :src="getImageUrl(category.image)"
              :alt="category.name"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              @error="handleImageError($event, category)"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500/10 to-secondary-500/10"
            >
              <i class="pi pi-box text-4xl text-theme-muted"></i>
            </div>
            <!-- Overlay on hover -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>

          <!-- Info -->
          <div class="p-3 sm:p-4">
            <h3 class="font-semibold text-theme-text text-sm sm:text-base group-hover:text-primary-500 transition-colors">
              {{ category.name }}
            </h3>
            <p v-if="category.description" class="text-xs text-theme-muted mt-1 line-clamp-2">
              {{ category.description }}
            </p>
            <p class="text-xs sm:text-sm text-theme-muted mt-1">
              {{ category.subcategories?.length || 0 }} subcategories
              <span class="ml-1">&middot; {{ getAdCount(category) }} listings</span>
            </p>
          </div>
        </button>
      </div>

      <!-- No search results -->
      <div v-if="!loading && filteredCategories.length === 0 && categorySearch" class="py-10 text-center">
        <i class="pi pi-search text-4xl text-theme-muted mb-3"></i>
        <p class="text-theme-muted">No categories match "{{ categorySearch }}"</p>
        <button @click="categorySearch = ''" class="mt-3 text-primary-600 hover:underline text-sm font-medium">Clear search</button>
      </div>
    </div>
  </div>
</template>
